// dermaPenData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const dermaPen: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Dermapen Treatment in Dubai UAE | Elite Body Home",
            "description": "Dermapen Treatment is a cutting-edge skin treatment that has gained immense popularity for its ability to rejuvenate the skin, improve texture, and address various skin concerns.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/dermapen/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Dermapen Treatment in Dubai UAE | Elite Body Home",
                "description": "Dermapen Treatment is a cutting-edge skin treatment that has gained immense popularity for its ability to rejuvenate the skin, improve texture, and address various skin concerns.",
                "url": "https://elitebodyhome.com/services/dermapen/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-23T16:44:48+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Dermapen Treatment",
                "image_type": "image/jpeg",
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Dermapen Treatment in Dubai UAE | Elite Body Home",
                "description": "Dermapen Treatment is a cutting-edge skin treatment that has gained immense popularity for its ability to rejuvenate the skin, improve texture, and address various skin concerns.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Dermapen"],
        "hero": {
            "title": "Dermapen Treatment",
            "subtitle": "World's Most Advanced Micro Needling Device - Dermapen",
            "description": "Dermapen treatment is one of the most advanced skin treatment systems, so popular nowadays in the beauty industry as well as dermatology clinics that work with skin rejuvenation, texture improvement and other skin problems. Using micro-needling technology to encourage collagen production, this minimally invasive procedure has become a popular solution for anyone who wants smooth, healthy, youthful skin."
        },
        "howItWorks": {
            "title": "What is Dermapen?",
            "description": "Dermapen is an advanced micro-needling device that creates controlled micro-injuries on the surface of the skin to stimulate the body's natural healing mechanism. This causes the body to produce collagen and elastin, which aid in the repair of skin damage and the reduction of prevalent skin conditions such as wrinkles, pimple scarring, and pigmentation. The Dermapen is small and precise, allowing for an effective delivery in targeted areas of the face and body."
        },
        "procedureSteps": {
            "heading": "What to Expect During a Dermapen Treatment",
            "para": "The treatment is uncomplicated, requiring just a handheld device that includes fine and adjustable needles. This process helps promote collagen production and improve the texture of the skin, as these needles puncture the skin at varying depths.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Consulting with a skincare professional is necessary to address your specific skin concerns and map out the best course of action.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Cleansing",
                    "desc": "Everything in the treatment area is sterilized.",
                    "icon": "fa-solid fa-soap"
                },
                {
                    "title": "Topical Anesthetic",
                    "desc": "To minimize discomfort during the procedure, a numbing cream is applied.",
                    "icon": "fa-solid fa-spray-can"
                },
                {
                    "title": "Micro-Needling",
                    "desc": "To do this, the Derma Pen device simply glides across the skin, forming controlled micro injuries.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "Advantages of Dermapen Treatment in Dubai",
            "subheading": "",
            "para": "It is evident that dermapen is effective for many skin problems. Whether you're struggling with acne scars, fine lines, or uneven skin tone, this treatment is meant to improve the look of your skin.",
            "items": [
                {
                    "title": "Increases Collagen Production",
                    "desc": "Helps to stimulate the natural production of collagen, increasing the firmness and elasticity of the skin.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Helps Diminish the Appearance of Wrinkles and Fine Lines",
                    "desc": "Fills in fine lines, leaving you looking more youthful.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "Minimizes Acne Scars",
                    "desc": "Are well known to minimize the visibility of both acne scars, skin texture and surface imperfections.",
                    "icon": "fa-solid fa-circle-notch"
                },
                {
                    "title": "Reduces Pore Size",
                    "desc": "Aids in tightening enlarged pores for a smoother skin texture.",
                    "icon": "fa-solid fa-compress"
                },
                {
                    "title": "Evens Skin Tone",
                    "desc": "Helps with pigmentation issues for a more even skin tone.",
                    "icon": "fa-solid fa-equals"
                },
                {
                    "title": "Improves Skin Texture",
                    "desc": "Keeps skin smooth, soft, and luminous due to improved skin texture.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "Who is Eligible for Dermapen?",
            "subheading": "",
            "para": "Dermapen treatment is appropriate for a broad scale clients that need to improve the look of their skin. But not everyone is good candidate for this therapy. There is the possibility that this procedure may not be suitable for you at all, and understanding who is most likely to benefit will help you achieve the results that you desire.",
            "items": [
                {
                    "title": "Individuals with Aging Skin",
                    "desc": "Those who want to diminish wrinkles, fine lines and sagging.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "People with Acne Scars",
                    "desc": "A great solution for those suffering from acne or similar skin conditions leading to scarring.",
                    "icon": "fa-solid fa-circle-dot"
                },
                {
                    "title": "Dark Spots and Uneven Skin Tone",
                    "desc": "Perfect for anyone looking to address pigmentation problems or uneven skin.",
                    "icon": "fa-solid fa-circle-half-stroke"
                },
                {
                    "title": "Sun-Damaged Skin",
                    "desc": "Assists to restore skin damaged through sun overload.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "People might have Enlarged Pores",
                    "desc": "Minimizes pore size and makes skin smooth.",
                    "icon": "fa-solid fa-expand"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "Dermapen Microneedling aftercare: Prepping your Skin Before Treatment",
            "para": "Preparing your skin beforehand is an important step to ensure your dermapen microneedling treatment will give you the best results, and to ensure a smooth recovery. Here are steps you can take in advance of your appointment.",
            "items": [
                {
                    "title": "Avoid Sun Exposure",
                    "desc": "Minimize sun exposure and do not tan for at least one week before treatment.",
                    "icon": "fa-solid fa-sun-slash"
                },
                {
                    "title": "Stop Using Retinoids",
                    "desc": "Do not use retinoid creams or any exfoliating products for 3 to 5 days before the procedure.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Hydrate Your Skin",
                    "desc": "In the days leading up to treatment, ensure that your skin is well moisturized.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Avoid Blood Thinners",
                    "desc": "Refrain from taking aspirin, ibuprofen or any blood-thinning medications, if you can, to minimize bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Consultation",
                    "desc": "Your provider will ask you about any medications and skin conditions that may impact your treatment during your consultation.",
                    "icon": "fa-solid fa-comments"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Patients see a noticeable improvement in skin texture and tone in just a few days. Collagen production, however, will continue for weeks, and your best results typically occur after a series of treatments. The skin looks smoother, firmer, and more radiant, while signs of ageing, scars, and pigmentation diminish.",
            "items": [
                {
                    "title": "Before & After Results",
                    "desc": "",
                    "icon": "fa-solid fa-images"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "",
                    "desc": "",
                    "icon": "fa-solid fa-water"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How soon will I see results?",
                    "answer": "Results vary, but most patients begin to see improvement in hair texture and reduction in shedding after the first few treatments. Significant regrowth can be seen after 3 to 6 months of continuous therapy."
                },
                {
                    "question": "Is the procedure painful?",
                    "answer": "The injections can cause mild discomfort, but most patients find it tolerable. A numbing cream can be applied to minimize any pain."
                },
                {
                    "question": "How many sessions will I need?",
                    "answer": "Typically, 3 to 6 sessions are recommended, spaced about 4 to 6 weeks apart, for optimal results. Your clinician will create a personalized treatment plan based on your hair loss condition."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "The treatment is generally safe, with minimal side effects such as mild swelling, redness, or bruising at the injection site. These usually subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "introductionImage": "",
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/ebh-before-and-after-67.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-2.jpg"
            ]
        },
        "product": {
            "id": 117,
            "title": "Dermapen Treatment",
            "price": 599,
            "description": "Dermapen is a cutting-edge skin treatment known for its ability to rejuvenate the skin, improve texture, and address a variety of skin concerns, including scars, wrinkles, and stretch marks."
        },
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "علاج ديرمابن في دبي، الإمارات العربية المتحدة | إيليت بودي هوم",
            "description": "علاج ديرمابن هو أحدث تقنيات العناية بالبشرة التي اكتسبت شعبية كبيرة لقدرتها على تجديد البشرة، تحسين الملمس، ومعالجة مختلف مشاكل الجلد.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/dermapen/",
            "og": {
                "locale": "ar_AE",
                "type": "article",
                "title": "علاج ديرمابن في دبي، الإمارات العربية المتحدة | إيليت بودي هوم",
                "description": "علاج ديرمابن هو أحدث تقنيات العناية بالبشرة التي اكتسبت شعبية كبيرة لقدرتها على تجديد البشرة، تحسين الملمس، ومعالجة مختلف مشاكل الجلد.",
                "url": "https://elitebodyhome.com/services/dermapen/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-23T16:44:48+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج ديرمابن",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "علاج ديرمابن في دبي، الإمارات العربية المتحدة | إيليت بودي هوم",
                "description": "علاج ديرمابن هو أحدث تقنيات العناية بالبشرة التي اكتسبت شعبية كبيرة لقدرتها على تجديد البشرة، تحسين الملمس، ومعالجة مختلف مشاكل الجلد.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-35.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "ديرمابن"],
        "hero": {
            "title": "علاج ديرمابن",
            "subtitle": "أحدث تقنية للوخز بالإبر الدقيقة في العالم - ديرمابن",
            "description": "يُعدّ علاج ديرمابن من أحدث تقنيات العناية بالبشرة، ويحظى بشعبية واسعة في مجال التجميل وعيادات الجلدية، حيث يُستخدم لتحسين نضارة البشرة وملمسها ومعالجة مختلف مشاكلها. تعتمد هذه التقنية على الوخز بالإبر الدقيقة لتحفيز إنتاج الكولاجين، مما يجعله إجراءً غير جراحي وفعّالاً لمن يسعون للحصول على بشرة ناعمة، وصحية، ومشرقة."
        },
        "howItWorks": {
            "title": "ما هو علاج ديرمابن؟",
            "description": "الديرمابن هو جهاز متطور لتقنية الوخز بالإبر الدقيقة، يعمل على إحداث جروح دقيقة مُتحكم بها على سطح الجلد لتحفيز آلية الشفاء الطبيعية في الجسم. يؤدي ذلك إلى تعزيز إنتاج الكولاجين والإيلاستين في البشرة، مما يساعد على إصلاح تلف البشرة والتخفيف من مشاكلها الشائعة مثل التجاعيد، وندبات حب الشباب، والتصبغات. يتميز جهاز الديرمابن بحجمه الصغير ودقته العالية، مما يجعله فعالاً في معالجة المناطق المستهدفة من الوجه والجسم."
        },
        "procedureSteps": {
            "heading": "ما الذي يمكن توقعه خلال جلسة ديرمابن؟",
            "para": "يعد ديرمابن علاجًا غير معقدًا، حيث يُستخدم جهاز صغير محمول باليد مزود بإبر دقيقة قابلة للتعديل. تعمل هذه التقنية على علاج مشاكل البشرة من خلال تحفيز إنتاج الكولاجين وتحسين ملمس البشرة من خلال وخز الجلد بعمق متغير.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يُنصح بمراجعة أخصائي العناية بالبشرة لمناقشة المشاكل التي تعاني منها بشرتك وتحديد الخطة العلاجية الأنسب لك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "التنظيف",
                    "desc": "يتم تعقيم أي أدوات في العيادة لضمان بيئة آمنة أثناء الإجراء.",
                    "icon": "fa-solid fa-soap"
                },
                {
                    "title": "التخدير الموضعي",
                    "desc": "يُستخدم كريم مخدر لتقليل الشعور بالألم أثناء الجلسة.",
                    "icon": "fa-solid fa-spray-can"
                },
                {
                    "title": "الوخز بالإبر الدقيقة",
                    "desc": "يتم تمرير جهاز ديرمابن بلطف على سطح الجلد، محدثًا جروح دقيقة محسوبة بدقة.",
                    "icon": "fa-solid fa-syringe"
                }
            ]
        },
        "benefits": {
            "heading": "مزايا علاج ديرمابن في دبي",
            "subheading": "",
            "para": "يُعدّ علاجًا فعّالًا للعديد من مشاكل البشرة. سواء كنت تعاني من ندبات حب الشباب، أو الخطوط الرفيعة، أو تفاوت لون البشرة، فإن هذا الإجراء مصمم لتحسين مظهر بشرتك.",
            "items": [
                {
                    "title": "تحفيز إنتاج الكولاجين",
                    "desc": "يساعد على تعزيز الإنتاج الطبيعي للكولاجين، مما يزيد من تماسك البشرة ومرونتها.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "تقليل التجاعيد والخطوط الرفيعة",
                    "desc": "يملأ الخطوط الرفيعة، مما يمنحك مظهراً أكثر شبابًا.",
                    "icon": "fa-solid fa-minus"
                },
                {
                    "title": "تخفيف ندبات حب الشباب",
                    "desc": "يُعرف بفعاليته في تقليل مظهر ندبات حب الشباب وتحسين ملمس البشرة والتخلص من عيوبها السطحية.",
                    "icon": "fa-solid fa-circle-notch"
                },
                {
                    "title": "علاج المسام الواسعة",
                    "desc": "يساعد في تصغير المسام الواسعة للحصول على بشرة أكثر نعومة.",
                    "icon": "fa-solid fa-compress"
                },
                {
                    "title": "توحيد لون البشرة",
                    "desc": "يُساهم في علاج التصبغات للحصول على لون بشرة موحد.",
                    "icon": "fa-solid fa-equals"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يجعل البشرة أكثر نعومة ونضارة وإشراقًا بفضل تحسين ملمسها العام.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "eligibility": {
            "heading": "من المرشح لعلاج الديرمابن؟",
            "subheading": "",
            "para": "يُعتبر علاج ديرمابن خيارًا مناسبًا لفئة واسعة من الأشخاص الذين يرغبون في تحسين مظهر بشرتهم. ومع ذلك، قد لا يكون هذا الإجراء مناسبًا للجميع، لذا فإن معرفة الفئات الأكثر استفادة منه سيساعدك في تحقيق النتائج التي تطمح إليها.",
            "items": [
                {
                    "title": "الأشخاص الذين يعانون من علامات التقدم في العمر",
                    "desc": "يعد إجراء مثالي لمن يرغب في تقليل التجاعيد، والخطوط الرفيعة.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "الأشخاص الذين يعانون من ندبات حب الشباب",
                    "desc": "يعتبر حل فعّال لمن يعاني من ندبات حب الشباب أو مشاكل جلدية مماثلة تؤدي إلى ندبات.",
                    "icon": "fa-solid fa-circle-dot"
                },
                {
                    "title": "الأشخاص الذين يعانون من البقع الداكنة وتفاوت لون البشرة",
                    "desc": "يناسب من يبحث عن علاج لمشاكل التصبغات وتفاوت لون البشرة.",
                    "icon": "fa-solid fa-circle-half-stroke"
                },
                {
                    "title": "الأشخاص الذين تعرضت بشرتهم لأضرار أشعة الشمس",
                    "desc": "يساعد في تجديد البشرة المتضررة من التعرض المفرط لأشعة الشمس.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "الأشخاص الذين يعانون من المسام الواسعة",
                    "desc": "يعمل على تصغير حجم المسام وتحسين ملمس البشرة لتصبح أكثر نعومة.",
                    "icon": "fa-solid fa-expand"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "العناية بالبشرة بعد جلسة الديرمابن: التحضير للعلاج",
            "para": "يُعد تحضير البشرة مسبقًا خطوة أساسية لضمان تحقيق أفضل النتائج من علاج ديرمابن بالوخز بالإبر الدقيقة، ولتسريع عملية التعافي بسلاسة. إليك بعض الخطوات التي يُنصح باتباعها قبل موعد الجلسة.",
            "items": [
                {
                    "title": "تجنب التعرض لأشعة الشمس",
                    "desc": "تجنب التعرض المفرط للشمس وجلسات التسمير (التان) لمدة لا تقل عن أسبوع قبل الجلسة.",
                    "icon": "fa-solid fa-sun-slash"
                },
                {
                    "title": "الامتناع عن استخدام الريتينويدات",
                    "desc": "امتنع عن استخدام كريمات الريتينويد أو أي منتجات تقشير البشرة لمدة تتراوح بين 3 إلى 5 أيام قبل الإجراء.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "حافظ على ترطيب البشرة",
                    "desc": "احرص على ترطيب بشرتك جيدًا في الأيام التي تسبق العلاج.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "تجنب مُميعات الدم",
                    "desc": "إذا أمكن، امتنع عن تناول الأسبرين والإيبوبروفين وأي أدوية مميعة للدم لتقليل احتمالية ظهور كدمات.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الاستشارة",
                    "desc": "سيقوم الطبيب المتخصص بمراجعة أي أدوية أو مشاكل جلدية قد تؤثر على العلاج خلال جلسة الاستشارة.",
                    "icon": "fa-solid fa-comments"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "يلاحظ المرضى تحسنًا واضحًا في ملمس البشرة وتوحيد لونها خلال أيام قليلة من العلاج. ومع ذلك، يستمر إنتاج الكولاجين لأسابيع، وتظهر أفضل النتائج عادةً بعد سلسلة من الجلسات. تصبح البشرة أكثر نعومة وتماسكًا وإشراقًا، مع تلاشي علامات التقدم في العمر، والندبات، والتصبغات.",
            "items": [
                {
                    "title": "النتائج قبل وبعد العلاج",
                    "desc": "",
                    "icon": "fa-solid fa-images"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "",
                    "desc": "",
                    "icon": "fa-solid fa-water"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "متى يمكنني رؤية النتائج؟",
                    "answer": "تختلف النتائج من شخص لآخر، لكن معظم المرضى يلاحظون تحسنًا في ملمس الشعر وتقليل التساقط بعد الجلسات الأولى. يمكن رؤية نتائج واضحة في نمو الشعر بعد 3 إلى 6 أشهر من العلاج المستمر."
                },
                {
                    "question": "هل الإجراء مؤلم؟",
                    "answer": "قد تسبب الحقن بعض الانزعاج البسيط، لكن معظم المرضى يجدونه محتملاً. يمكن استخدام كريم مخدر لتقليل أي ألم."
                },
                {
                    "question": "كم عدد الجلسات التي سأحتاجها؟",
                    "answer": "عادةً ما نوصي بـ 3 إلى 6 جلسات، بفاصل زمني 4 إلى 6 أسابيع بين الجلسة والأخرى للحصول على أفضل النتائج. سيضع الطبيب خطة علاجية مخصصة بناءً على حالة تساقط الشعر."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "العلاج آمن بشكل عام، مع آثار جانبية بسيطة مثل تورم خفيف، احمرار، أو كدمات في مكان الحقن. تزول هذه الأعراض عادةً خلال بضعة أيام."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }

};
